import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import DashboardLayout from "./DashboardLayout";

const API = "/backend/public/api";

const pageShellStyle = {
  display: "grid",
  gap: "20px",
};

const heroStyle = {
  background: "linear-gradient(135deg, #4e1b24 0%, #7c2d3a 48%, #c2410c 100%)",
  borderRadius: "28px",
  padding: "28px",
  color: "#ffffff",
  boxShadow: "0 28px 60px rgba(124, 45, 58, 0.20)",
};

const panelStyle = {
  background: "#ffffff",
  borderRadius: "22px",
  padding: "22px",
  border: "1px solid rgba(148, 163, 184, 0.16)",
  boxShadow: "0 18px 38px rgba(15, 23, 42, 0.08)",
};

const inputStyle = {
  minHeight: "46px",
  borderRadius: "14px",
  border: "1px solid rgba(148, 163, 184, 0.28)",
  padding: "10px 14px",
  width: "100%",
};

const textAreaStyle = {
  ...inputStyle,
  minHeight: "96px",
  resize: "vertical",
};

function StatusChip({ status }) {
  const tone =
    status === "approved"
      ? { color: "#166534", background: "#dcfce7" }
      : status === "hold"
      ? { color: "#92400e", background: "#fef3c7" }
      : status === "rejected"
      ? { color: "#b91c1c", background: "#fee2e2" }
      : { color: "#1d4ed8", background: "#dbeafe" };

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "6px 10px",
        borderRadius: "999px",
        fontSize: "12px",
        fontWeight: 700,
        textTransform: "capitalize",
        ...tone,
      }}
    >
      {status || "pending"}
    </span>
  );
}

function FrontOfficeHero({ title, description, icon }) {
  return (
    <section style={heroStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: "18px", flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ maxWidth: "760px" }}>
          <p style={{ margin: 0, fontSize: "12px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.78 }}>
            Front Office
          </p>
          <h1 style={{ margin: "10px 0 12px", fontSize: "34px", lineHeight: 1.1 }}>{title}</h1>
          <p style={{ margin: 0, color: "rgba(255,255,255,0.84)", lineHeight: 1.75 }}>{description}</p>
        </div>

        <div
          style={{
            width: "86px",
            height: "86px",
            borderRadius: "24px",
            background: "rgba(255,255,255,0.16)",
            border: "1px solid rgba(255,255,255,0.18)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "36px",
            flexShrink: 0,
          }}
        >
          <i className={`bi ${icon}`}></i>
        </div>
      </div>
    </section>
  );
}

export function AdminPostalOrderPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    franchise_name: "",
    franchise_code: "",
    status: "",
  });
  const [drafts, setDrafts] = useState({});
  const [busyId, setBusyId] = useState(null);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/postal-orders`, {
        params: {
          ...filters,
          _ts: Date.now(),
        },
      });
      setRows(res.data || []);
    } catch (error) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const runFilter = () => {
    loadRows();
  };

  const setDraftValue = (id, key, value) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        [key]: value,
      },
    }));
  };

  const handleAction = async (id, action) => {
    const draft = drafts[id] || {};

    if ((action === "hold" || action === "reject") && !String(draft.status_remark || "").trim()) {
      window.alert("Hold বা Reject করতে remarks দিতে হবে");
      return;
    }

    if (action === "approve" && !String(draft.tracking_link || "").trim()) {
      window.alert("Approve করতে tracking link দিতে হবে");
      return;
    }

    try {
      setBusyId(id);
      await axios.post(`${API}/postal-orders/${id}/action`, {
        action,
        dispatch_remarks: draft.dispatch_remarks || "",
        status_remark: draft.status_remark || "",
        tracking_link: draft.tracking_link || "",
      });
      await loadRows();
    } catch (error) {
      window.alert(error.response?.data?.message || "Postal order action failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <DashboardLayout>
      <div style={pageShellStyle}>
        <FrontOfficeHero
          title="Postal Order"
          description="Franchise panel থেকে আসা postal order এখানে serial-wise show হবে। Dispatch remarks, approve, hold, reject এবং tracking workflow এখান থেকেই control করা যাবে."
          icon="bi-envelope-open-fill"
        />

        <section style={panelStyle}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px", marginBottom: "16px" }}>
            <input
              placeholder="Filter by Franchise Name"
              value={filters.franchise_name}
              onChange={(e) => setFilters((prev) => ({ ...prev, franchise_name: e.target.value }))}
              style={inputStyle}
            />
            <input
              placeholder="Filter by Franchise Code"
              value={filters.franchise_code}
              onChange={(e) => setFilters((prev) => ({ ...prev, franchise_code: e.target.value }))}
              style={inputStyle}
            />
            <select value={filters.status} onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))} style={inputStyle}>
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="hold">Hold</option>
              <option value="rejected">Reject</option>
            </select>
          </div>

          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "18px" }}>
            <button className="btn btn-dark" type="button" onClick={runFilter}>Apply Filter</button>
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={() => {
                setFilters({ franchise_name: "", franchise_code: "", status: "" });
                setTimeout(loadRows, 0);
              }}
            >
              Reset
            </button>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  <th>Sl No</th>
                  <th>Requirement</th>
                  <th>Dispatch Remarks</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {!loading && rows.map((row, index) => (
                  <tr key={row.id}>
                    <td>{index + 1}</td>
                    <td style={{ minWidth: "340px" }}>
                      <strong style={{ display: "block" }}>{row.requirement}</strong>
                      <div style={{ marginTop: "8px", color: "#64748b", fontSize: "13px", lineHeight: 1.65 }}>
                        <div><strong>Franchise Name:</strong> {row.franchise_name || "-"}</div>
                        <div><strong>Franchise Code:</strong> {row.franchise_code || "-"}</div>
                        <div><strong>Address:</strong> {row.franchise_address || "-"}</div>
                      </div>
                      <div style={{ marginTop: "8px" }}>
                        <StatusChip status={row.status} />
                      </div>
                      {row.status_remark ? <div style={{ marginTop: "8px", color: "#7c2d3a", fontSize: "13px" }}><strong>Remarks:</strong> {row.status_remark}</div> : null}
                    </td>
                    <td style={{ minWidth: "260px" }}>
                      <textarea
                        style={textAreaStyle}
                        placeholder="Dispatch remarks"
                        value={drafts[row.id]?.dispatch_remarks ?? row.dispatch_remarks ?? ""}
                        onChange={(e) => setDraftValue(row.id, "dispatch_remarks", e.target.value)}
                      />
                      <textarea
                        style={{ ...textAreaStyle, marginTop: "10px", minHeight: "76px" }}
                        placeholder="Remarks for Hold / Reject"
                        value={drafts[row.id]?.status_remark ?? row.status_remark ?? ""}
                        onChange={(e) => setDraftValue(row.id, "status_remark", e.target.value)}
                      />
                      <input
                        style={{ ...inputStyle, marginTop: "10px" }}
                        placeholder="Tracking link for approve"
                        value={drafts[row.id]?.tracking_link ?? row.tracking_link ?? ""}
                        onChange={(e) => setDraftValue(row.id, "tracking_link", e.target.value)}
                      />
                    </td>
                    <td style={{ minWidth: "230px" }}>
                      <div style={{ display: "grid", gap: "10px" }}>
                        <button className="btn btn-success" type="button" disabled={busyId === row.id} onClick={() => handleAction(row.id, "approve")}>
                          {busyId === row.id ? "Working..." : "Approve"}
                        </button>
                        <button className="btn btn-warning" type="button" disabled={busyId === row.id} onClick={() => handleAction(row.id, "hold")}>
                          Hold
                        </button>
                        <button className="btn btn-danger" type="button" disabled={busyId === row.id} onClick={() => handleAction(row.id, "reject")}>
                          Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && rows.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center py-4 text-muted">No postal order found.</td>
                  </tr>
                ) : null}
                {loading ? (
                  <tr>
                    <td colSpan="4" className="text-center py-4 text-muted">Loading postal orders...</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}

export function AdminPostalDispatchPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    franchise_name: "",
    franchise_code: "",
  });

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/postal-dispatch`, {
        params: {
          ...filters,
          _ts: Date.now(),
        },
      });
      setRows(res.data || []);
    } catch (error) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  return (
    <DashboardLayout>
      <div style={pageShellStyle}>
        <FrontOfficeHero
          title="Postal Dispatch"
          description="Approved postal order rows automatically postal dispatch register-এ show হবে। Tracking link এখানেও auto carry করবে."
          icon="bi-send-fill"
        />

        <section style={panelStyle}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px", marginBottom: "16px" }}>
            <input
              placeholder="Filter by Franchise Name"
              value={filters.franchise_name}
              onChange={(e) => setFilters((prev) => ({ ...prev, franchise_name: e.target.value }))}
              style={inputStyle}
            />
            <input
              placeholder="Filter by Franchise Code"
              value={filters.franchise_code}
              onChange={(e) => setFilters((prev) => ({ ...prev, franchise_code: e.target.value }))}
              style={inputStyle}
            />
          </div>

          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "18px" }}>
            <button className="btn btn-dark" type="button" onClick={loadRows}>Apply Filter</button>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  <th>Sl No</th>
                  <th>Requirement</th>
                  <th>Dispatch Remarks</th>
                  <th>Tracking Logo</th>
                </tr>
              </thead>
              <tbody>
                {!loading && rows.map((row, index) => (
                  <tr key={row.id}>
                    <td>{index + 1}</td>
                    <td style={{ minWidth: "340px" }}>
                      <strong style={{ display: "block" }}>{row.requirement}</strong>
                      <div style={{ marginTop: "8px", color: "#64748b", fontSize: "13px", lineHeight: 1.65 }}>
                        <div><strong>Franchise Name:</strong> {row.franchise_name || "-"}</div>
                        <div><strong>Franchise Code:</strong> {row.franchise_code || "-"}</div>
                        <div><strong>Address:</strong> {row.franchise_address || "-"}</div>
                      </div>
                    </td>
                    <td style={{ minWidth: "220px" }}>{row.dispatch_remarks || "-"}</td>
                    <td>
                      {row.tracking_link ? (
                        <a href={row.tracking_link} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm">
                          <i className="bi bi-box-arrow-up-right"></i> Tracking
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))}
                {!loading && rows.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center py-4 text-muted">No dispatch row found.</td>
                  </tr>
                ) : null}
                {loading ? (
                  <tr>
                    <td colSpan="4" className="text-center py-4 text-muted">Loading dispatch list...</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}



